import {
    doc,
    getDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { User, UpdateUserData } from '@/types';
import { convertTimestamps, TIMESTAMP_FIELDS } from '@/utils/firestore';

export class UserService {
    static async createUser(data: Omit<User, 'createdAt' | 'updatedAt'>): Promise<User> {
        const userRef = doc(db, 'users', data.id);

        // Check if user exists first to avoid overwriting (idempotency)
        const snap = await getDoc(userRef);
        if (snap.exists()) {
            return convertTimestamps(snap.data() as User, TIMESTAMP_FIELDS.user as any);
        }

        const newUser = {
            ...data,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        };

        await setDoc(userRef, newUser);

        // Return the object with Dates instead of serverTimestamp placeholders (approximation for UI)
        return {
            ...data,
            createdAt: new Date(),
            updatedAt: new Date(),
        } as User;
    }

    static async getUser(userId: string): Promise<User | null> {
        const userRef = doc(db, 'users', userId);
        const snap = await getDoc(userRef);

        if (snap.exists()) {
            return convertTimestamps(snap.data() as User, TIMESTAMP_FIELDS.user as any);
        }
        return null;
    }

    static async updateUser(userId: string, updates: UpdateUserData): Promise<void> {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            ...updates,
            updatedAt: serverTimestamp(),
        });
    }

    static async deleteUser(userId: string): Promise<void> {
        const userRef = doc(db, 'users', userId);
        await deleteDoc(userRef);
    }

    /**
     * Find a user by exact email match.
     * Note: Requires a composite index if we were sorting, but simple equality usually fine.
     * However, queries on non-ID fields might need an index if collection is large.
     */
    static async searchUserByEmail(email: string): Promise<User | null> {
        const { collection, query, where, getDocs, limit } = await import('firebase/firestore');
        const q = query(collection(db, 'users'), where('email', '==', email), limit(1));
        const snap = await getDocs(q);

        if (!snap.empty) {
            return convertTimestamps(snap.docs[0].data() as User, TIMESTAMP_FIELDS.user as any);
        }
        return null;
    }

    /**
     * Get multiple users by ID.
     * Firestore 'in' query supports up to 10 items.
     * For larger lists, we should parallelize getDoc or multiple queries. 
     * For MVP with small collaborator lists, individual gets or chunks are fine.
     * Let's use documentId 'in' query for up to 10.
     */
    static async getUsersByIds(userIds: string[]): Promise<User[]> {
        if (!userIds.length) return [];

        // Simple parallel fetch for now (easier than batched 'in' queries for potentially >10 IDs)
        // Optimization: simple cache or batched queries later.
        const promises = userIds.map(id => this.getUser(id));
        const results = await Promise.all(promises);
        return results.filter((u): u is User => u !== null);
    }
}
